import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  FormGroup,
  Grid,
  ImageList,
  ImageListItem,
  InputLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomRHFPhoneInput from "../../.reUseableComponents/TextField/CustomRHFPhoneInput";
import SelectComponent from "../../.reUseableComponents/TextField/SelectComponent";
import TextAreaComponent from "../../.reUseableComponents/TextField/TextAreaComponent";
import {
  CreateOrderFromListing,
  GetAllCountry,
  GetProductDetailByLinkToken,
} from "../../api/AxiosInterceptors";
import { styles, styleSheet, theme } from "../../assets/styles/style";
import { setThisKeyCookie } from "../../utilities/cookies";
import { EnumOptions, inputTypesEnum } from "../../utilities/enum";
import {
  addressSchemaEnum,
  SchemaTextField,
  useGetAddressSchema,
} from "../../utilities/helpers/addressSchema";
import {
  CicrlesLoading,
  getLowerCase,
  placeholders,
  useGetLocationOrPathOrSearchParams,
  useNavigateSetState,
} from "../../utilities/helpers/Helpers";
import { successNotification } from "../../utilities/toast";
import UtilityClass from "../../utilities/UtilityClass";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Dialog, IconButton } from "@mui/material";

export function ProductImages({ productLinkData } = {}) {
  const medias = productLinkData?.data?.ProductMedias ?? [];
  const featureImage = productLinkData?.data?.FeatureImage ?? "";
  const themeForBreakPoint = useTheme();
  const isTabletOrMobile = useMediaQuery(
    themeForBreakPoint.breakpoints.down("sm")
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openViewer, setOpenViewer] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const SWIPE_THRESHOLD = 50;

  useEffect(() => {
    if (featureImage && medias.length) {
      const index = medias.findIndex((m) => m.ImageUrl === featureImage);
      setSelectedIndex(index >= 0 ? index : 0);
    }
  }, [featureImage, medias]);

  const handleNext = useCallback(() => {
    setSelectedIndex((prev) => (prev === medias.length - 1 ? 0 : prev + 1));
  }, [medias.length]);

  const handlePrev = useCallback(() => {
    setSelectedIndex((prev) => (prev === 0 ? medias.length - 1 : prev - 1));
  }, [medias.length]);

  /* 🔹 Keyboard navigation */
  useEffect(() => {
    if (!openViewer) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") setOpenViewer(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openViewer, handleNext, handlePrev]);

  /* 🔹 Swipe handlers */
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      diff > 0 ? handleNext() : handlePrev();
    }
  };

  if (!productLinkData?.data) return null;

  return (
    <>
      {/* Thumbnails + Feature Image */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexDirection: isTabletOrMobile ? "row-reverse" : "row",
        }}
      >
        {medias.length > 0 && (
          <ImageList
            cols={1}
            gap={6}
            sx={{
              width: 60,
              maxHeight: 220,
              overflowY: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {medias.map((dt, index) => (
              <ImageListItem
                key={index}
                onClick={() => setSelectedIndex(index)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "6px",
                  border:
                    selectedIndex === index
                      ? "2px solid #1976d2"
                      : "1px solid transparent",
                  borderRadius: "6px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={dt.ImageUrl}
                  alt="thumbnail"
                  style={{ width: 60, objectFit: "contain" }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
        <Box
          component="img"
          src={medias[selectedIndex]?.ImageUrl ?? featureImage}
          alt="Selected product"
          onClick={() => setOpenViewer(true)}
          sx={{
            width: 200,
            height: 200,
            cursor: "zoom-in",
            borderRadius: "10px",
            objectFit: "contain",
            boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          }}
        />
      </Box>

      <Dialog fullScreen open={openViewer} onClose={() => setOpenViewer(false)}>
        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            height: "100vh",
            width: "100vw",
            backgroundColor: "black",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            userSelect: "none",
          }}
        >
          {/* Close */}
          <IconButton
            onClick={() => setOpenViewer(false)}
            sx={{ position: "absolute", top: 16, right: 16, color: "white" }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous */}
          <IconButton
            onClick={handlePrev}
            sx={{ position: "absolute", left: 16, color: "white" }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>

          {/* Image */}
          <Box
            component="img"
            src={medias[selectedIndex]?.ImageUrl ?? featureImage}
            alt="Full view"
            sx={{
              maxWidth: "95%",
              maxHeight: "95%",
              objectFit: "contain",
            }}
          />

          {/* Next */}
          <IconButton
            onClick={handleNext}
            sx={{ position: "absolute", right: 16, color: "white" }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
}

const OrderTake = () => {
  const LanguageReducer = useSelector((state) => state.LanguageReducer);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
    control,
    unregister,
  } = useForm({});
  const { setNavigateState } = useNavigateSetState();
  const handleFocus = (event) => event.target.select();
  const themeForBreakPoint = useTheme();
  const isTabletOrMobile = useMediaQuery(
    themeForBreakPoint.breakpoints.down("md")
  );
  const {
    selectedAddressSchema,
    addressSchemaSelectData,
    addressSchemaInputData,
    handleSetSchema,
    handleChangeInputAddressSchema,
    handleChangeSelectAddressSchemaAndGetOptions,
  } = useGetAddressSchema(setValue, true);
  const schemaFieldsLength = [
    ...addressSchemaSelectData,
    ...addressSchemaInputData,
  ].length;

  const { path } = useGetLocationOrPathOrSearchParams();
  const replacedPath = path.replace("/", "");
  const [allCountries, setAllCountries] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [textValue, setTextValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [productLinkData, setProductLinkData] = useState({
    loading: false,
    data: {},
  });
  const [actualPrice, setActualPrice] = useState(0);

  const tax = 0;

  let getAllCountry = async () => {
    let res = await GetAllCountry({});
    if (res.data.result != null) setAllCountries(res.data.result);
  };

  const handleToggleChange = (optionId, newValue) => {
    if (newValue !== null) {
      setSelectedOptions((prev) => ({
        ...prev,
        [optionId]: newValue,
      }));
    }
  };

  const groupedOptions = (productLinkData?.data?.ProductOptions || []).reduce(
    (acc, item) => {
      if (!acc[item.ProductOptionId]) {
        acc[item.ProductOptionId] = {
          name: item.Name,
          options: [],
        };
      }
      acc[item.ProductOptionId].options.push(item);
      return acc;
    },
    {}
  );

  const PlaceOrder = async (data) => {
    const seletedConvertOption = Object.values(selectedOptions).join("/");

    const normalizeVariant = (variant) =>
      variant
        .split("/")
        .map((v) => v.toLowerCase().trim())
        .sort()
        .join("/");

    const normalizedSelected = normalizeVariant(seletedConvertOption);

    const selectedProdunctVarient = productLinkData?.data?.ProductStocks.find(
      (dt) => {
        return normalizeVariant(dt.VarientOption) === normalizedSelected;
      }
    );

    const productVarient = {
      productId: productLinkData.data.ProductId,
      productStockId: selectedProdunctVarient?.ProductStockId,
      price: selectedProdunctVarient?.Price,
      description: "",
      remarks: "",
      quantity: quantity,
      discount: 0,
      StockSku: selectedProdunctVarient?.SKU,
    };
    const itemValue = productLinkData?.data?.Price + tax;

    const param = {
      orderList: [
        {
          storeId: productLinkData?.data?.StoreId,
          SaleChannelConfigId: "",
          channelId: data.storeChannel?.id,
          orderTypeId: 2,
          orderDate: UtilityClass.getFormatedDateWithoutTime(new Date()),
          description: data.description,
          remarks: textValue,
          amount: productLinkData?.data?.Price * quantity,
          cShippingCharges: 0,
          paymentStatusId: 2,
          weight: data.weight,
          itemValue: itemValue,
          orderRequestVia: 1,
          paymentMethodId: 2,
          stationId: selectedProdunctVarient?.ProductStationId,
          discount: 0,
          vat: 0,
          refNo: data?.refNo,
          orderNote: {
            note: textValue,
          },
          orderAddress: {
            countryId: selectedAddressSchema.country,
            cityId: selectedAddressSchema.city,
            areaId: selectedAddressSchema.area,
            streetAddress: selectedAddressSchema.streetAddress,
            streetAddress2: selectedAddressSchema.streetAddress2,
            houseNo: selectedAddressSchema.houseNo,
            buildingName: selectedAddressSchema.buildingName,
            landmark: selectedAddressSchema.landmark,
            provinceId: selectedAddressSchema.province,
            pinCodeId: selectedAddressSchema.pinCode,
            stateId: selectedAddressSchema.state,
            zip: selectedAddressSchema.zip,
            addressTypeId: 0,
            latitude: getValues("lat&long")?.split(",")[0] || null,
            longitude: getValues("lat&long")?.split(",")[1] || null,
            orderAddressId: 0,
            customerName: data.customerName,
            email: data.email,
            mobile1: data.mobile1,
            mobile2: data.mobile2 || null,
          },
          orderTaxes: [],
          orderBoxs: [],
          orderItems: [productVarient],
        },
      ],
    };
    console.log(param);
    setLoading(true);
    CreateOrderFromListing(param)
      .then((res) => {
        if (!res?.data?.isSuccess) {
          UtilityClass.showErrorNotificationWithDictionary(res?.data?.errors);
        } else {
          successNotification("Order Placed Successfully");
          reset();
          const stData = {
            order: res?.data?.result?.data[0],
          };
          let oNavaigate = "/Order-Placed";
          setNavigateState(oNavaigate, {
            stData,
          });
        }
      })
      .catch((e) => {
        console.log("e", e);
        UtilityClass.showErrorNotificationWithDictionary(
          e?.response?.data?.errors
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllCountry();
  }, []);

  useEffect(() => {
    const fetchProductData = async () => {
      setProductLinkData((prev) => ({
        ...prev,
        loading: true,
      }));
      const response = await GetProductDetailByLinkToken(replacedPath);
      if (response?.data?.isSuccess) {
        setThisKeyCookie("ClientId", response.data.result.ClientId);
        setProductLinkData((prev) => ({
          ...prev,
          loading: false,
          data: response?.data?.result || [],
        }));
      } else {
        setProductLinkData((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    };
    fetchProductData();
  }, []);

  useEffect(() => {
    setActualPrice(productLinkData?.data?.Price);
  }, [productLinkData]);

  useEffect(() => {
    const seletedConvertOption = Object.values(selectedOptions).join("/");

    const normalizeVariant = (variant) =>
      variant
        .split("/")
        .map((v) => v.toLowerCase().trim())
        .sort()
        .join("/");

    const normalizedSelected = normalizeVariant(seletedConvertOption);

    const selectedProdunctVarient = productLinkData?.data?.ProductStocks?.find(
      (dt) => {
        return normalizeVariant(dt.VarientOption) === normalizedSelected;
      }
    );
    if (selectedProdunctVarient !== undefined) {
      setActualPrice(selectedProdunctVarient?.Price);
    }
  }, [selectedOptions]);

  return (
    <>
      {productLinkData.loading ? (
        <CicrlesLoading />
      ) : productLinkData.data &&
        Object.keys(productLinkData.data).length > 0 ? (
        <Box sx={{ height: "700px" }}>
          <Grid container spacing={1} p={2} paddingBottom={"0px !important"}>
            <Grid item md={5} sm={12} xs={12}>
              <Card
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
                  background: theme.backgroundGradient,
                  overflow: "hidden",
                  border: `1px solid ${theme.divider}`,
                  m: 1,
                  transition: "transform 0.3s ease",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.primary,
                    color: theme.white,
                  }}
                >
                  <Typography variant="h6" fontWeight={600} align="center">
                    Product Details
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    paddingBottom: { lg: "0px", md: "0px", sm: "16px!import" },
                    paddingTop: { lg: "0px", md: "0px", sm: "16px!import" },
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <ProductImages productLinkData={productLinkData} />

                  <CardContent
                    sx={{
                      p: 0,
                      paddingBottom: "0px !important",
                      flex: "1",
                      pl: { sm: 3, xs: 1 },
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: {
                        lg: "300px",
                        md: "300px",
                        sm: "auto",
                        xs: "auto",
                      },
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      gutterBottom
                      variant="h5"
                      component="div"
                      fontWeight={600}
                      color={theme.text}
                    >
                      {productLinkData?.data?.ProductName}
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      color={theme.text}
                      sx={{ mb: 0.5 }}
                    >
                      Price: {actualPrice}
                    </Typography>

                    {Object.entries(groupedOptions).map(
                      ([optionId, { name, options }]) => (
                        <Box key={optionId}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={500}
                            color={theme.lightText}
                            sx={{ mb: 0.5 }}
                          >
                            {name}:
                          </Typography>

                          <FormGroup row>
                            <ToggleButtonGroup
                              color="primary"
                              value={selectedOptions[optionId] || ""}
                              exclusive
                              onChange={(e, newValue) =>
                                handleToggleChange(optionId, newValue)
                              }
                              aria-label={`Options for ${name}`}
                              sx={{
                                backgroundColor: theme.white,
                                "& .MuiToggleButton-root.Mui-selected": {
                                  backgroundColor: theme.primary,
                                  color: theme.white,
                                  "&:hover": {
                                    backgroundColor: theme.primary,
                                  },
                                },
                              }}
                            >
                              {options.map((opt) => (
                                <ToggleButton
                                  sx={{
                                    px: 1,
                                    py: 0.5,
                                    fontSize: "0.8rem",
                                    minHeight: "32px",
                                    minWidth: "40px",
                                    borderRadius: "6px",
                                    fontWeight: 500,
                                    textTransform: "inherit",
                                    border: `1px solid ${theme.divider}`,
                                    "&:hover": {
                                      borderColor: theme.primary,
                                    },
                                  }}
                                  key={opt.OptionValue}
                                  value={opt.OptionValue}
                                >
                                  {opt.OptionValue}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          </FormGroup>
                        </Box>
                      )
                    )}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        color={theme.lightText}
                        sx={{ mb: 0.5 }}
                      >
                        Quantity:
                      </Typography>

                      <FormGroup row>
                        <ToggleButtonGroup
                          value={quantity}
                          exclusive
                          onChange={(e, newValue) => {
                            if (newValue !== null) setQuantity(newValue);
                          }}
                          sx={{
                            backgroundColor: theme.white,
                            "& .MuiToggleButton-root.Mui-selected": {
                              backgroundColor: theme.primary,
                              color: theme.white,
                              "&:hover": {
                                backgroundColor: theme.primaryLight,
                              },
                            },
                          }}
                        >
                          <ToggleButton
                            value={quantity - 1}
                            disabled={quantity <= 1}
                            sx={{
                              px: 1,
                              py: 0.5,
                              fontSize: "0.8rem",
                              minHeight: "32px",
                              minWidth: "40px",
                              borderRadius: "6px",
                              fontWeight: 500,
                              textTransform: "inherit",
                              border: `1px solid ${theme.divider}`,
                              "&:hover": {
                                borderColor: theme.primary,
                              },
                            }}
                          >
                            –
                          </ToggleButton>

                          <ToggleButton
                            value={quantity}
                            disabled
                            sx={{
                              px: 1,
                              py: 0.5,
                              fontSize: "0.8rem",
                              minHeight: "32px",
                              minWidth: "40px",
                              borderRadius: "6px",
                              fontWeight: 500,
                              textTransform: "inherit",
                              border: `1px solid ${theme.divider}`,
                              "&.Mui-disabled": {
                                color: theme.text,
                              },
                            }}
                          >
                            {quantity}
                          </ToggleButton>

                          <ToggleButton
                            value={quantity + 1}
                            sx={{
                              px: 1,
                              py: 0.5,
                              fontSize: "0.8rem",
                              minHeight: "32px",
                              minWidth: "40px",
                              borderRadius: "6px",
                              fontWeight: 500,
                              textTransform: "inherit",
                              border: `1px solid ${theme.divider}`,
                              "&:hover": {
                                borderColor: theme.primary,
                              },
                            }}
                          >
                            +
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </FormGroup>
                    </Box>
                  </CardContent>
                </Box>
                {!isTabletOrMobile && (
                  <Box sx={{ backgroundColor: theme.secondary, pt: 1 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      align="center"
                      color={theme.primary}
                      sx={{ my: 0.5 }}
                    >
                      Order Summary
                    </Typography>

                    <CardContent sx={{ pt: 1 }}>
                      <Box
                        sx={{
                          backgroundColor: theme.white,
                          borderRadius: "12px",
                          p: 2,
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="h6" color={theme.text}>
                              Price
                            </Typography>
                            <Tooltip title="Price before taxes and shipping">
                              <HelpOutlineIcon
                                sx={{ fontSize: 16, color: theme.lightText }}
                              />
                            </Tooltip>
                          </Box>
                          <Typography
                            variant="h6"
                            fontWeight={500}
                            color={theme.text}
                          >
                            {productLinkData?.data?.Currency} {actualPrice}
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="h6" color={theme.text}>
                              Subtotal
                            </Typography>
                            <Tooltip title="Subtotal before taxes and shipping">
                              <HelpOutlineIcon
                                sx={{ fontSize: 16, color: theme.lightText }}
                              />
                            </Tooltip>
                          </Box>
                          <Typography
                            variant="h6"
                            fontWeight={500}
                            color={theme.text}
                          >
                            {productLinkData?.data?.Currency}{" "}
                            {actualPrice * quantity}
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <Typography color={theme.text} variant="h6">
                            Estimated Delivery &amp; Handling
                          </Typography>
                          <Typography
                            fontWeight={500}
                            color={theme.success}
                            variant="h6"
                          >
                            Free
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography color={theme.text} variant="h6">
                              Estimated Taxes
                            </Typography>
                            <Tooltip title="Calculated based on your location">
                              <HelpOutlineIcon
                                sx={{ fontSize: 16, color: theme.lightText }}
                              />
                            </Tooltip>
                          </Box>
                          <Typography
                            fontWeight={500}
                            color={theme.text}
                            variant="h6"
                          >
                            {productLinkData?.data?.Currency} {tax}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color={theme.text}
                          >
                            Total
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color={theme.primary}
                          >
                            {productLinkData?.data?.Currency}{" "}
                            {actualPrice * quantity + tax}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item md={7} sm={12} xs={12}>
              <Box sx={styles.formContainer}>
                <Card sx={styles.card}>
                  <Box sx={styles.cardHeader}>
                    <Typography variant="h5" component="h2" align="center">
                      Customer Information
                    </Typography>
                  </Box>
                  <CardContent
                    sx={{
                      ...styles.cardContent,
                      height: {
                        lg: "585px",
                        md: "585px",
                        sm: "auto",
                        xs: "auto",
                      },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item md={6} sm={6} xs={12}>
                        <InputLabel required sx={styleSheet.inputLabel}>
                          Customer Name
                        </InputLabel>
                        <TextField
                          type="text"
                          placeholder={placeholders.name}
                          onFocus={handleFocus}
                          size="small"
                          fullWidth
                          variant="outlined"
                          id="customerName"
                          name="customerName"
                          sx={styles.inputField}
                          {...register("customerName", {
                            required: {
                              value: true,
                              message:
                                LanguageReducer?.languageType
                                  ?.FIELD_REQUIRED_TEXT,
                            },
                            pattern: {
                              value: /^(?!\s*$).+/,
                              message:
                                LanguageReducer?.languageType
                                  ?.INPUT_SHOULD_NOT_BE_ONLY_SPACES,
                            },
                          })}
                          error={Boolean(errors.customerName)}
                          helperText={errors.customerName?.message}
                        />
                      </Grid>
                      <Grid item md={6} sm={6} xs={12}>
                        <InputLabel sx={styleSheet.inputLabel}>
                          Email
                        </InputLabel>
                        <TextField
                          placeholder={placeholders.email}
                          onFocus={handleFocus}
                          type="email"
                          size="small"
                          id="email"
                          name="email"
                          fullWidth
                          variant="outlined"
                          sx={styles.inputField}
                          {...register("email", {
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message:
                                LanguageReducer?.languageType
                                  ?.INVALID_EMAIL_TOAST,
                            },
                          })}
                          error={Boolean(errors.email)}
                          helperText={errors.email?.message}
                        />
                      </Grid>
                      <Grid item md={6} sm={6} xs={12}>
                        <InputLabel required sx={styleSheet.inputLabel}>
                          Phone No.
                        </InputLabel>
                        <CustomRHFPhoneInput
                          error={
                            LanguageReducer?.languageType?.FIELD_REQUIRED_TEXT
                          }
                          name="mobile1"
                          control={control}
                          selectedCountry={getValues("country")?.mapCountryCode}
                          required
                          borderRadius={"8px"}
                        />
                      </Grid>
                      <Grid item md={6} sm={6} xs={12}>
                        <InputLabel required sx={styleSheet.inputLabel}>
                          Country
                        </InputLabel>
                        <SelectComponent
                          name="country"
                          control={control}
                          options={allCountries}
                          getOptionLabel={(option) => option?.name}
                          isRHF={true}
                          required={true}
                          optionLabel={EnumOptions.COUNTRY.LABEL}
                          optionValue={EnumOptions.COUNTRY.VALUE}
                          {...register("country", {
                            required: {
                              value: true,
                            },
                          })}
                          value={getValues("country")}
                          onChange={(event, newValue) => {
                            const resolvedId = newValue ? newValue : null;
                            handleSetSchema(
                              "country",
                              resolvedId,
                              setValue,
                              unregister
                            );
                          }}
                          errors={errors}
                        />
                      </Grid>
                      {[
                        ...addressSchemaSelectData,
                        ...addressSchemaInputData,
                      ].map((input, index, arr) => (
                        <Grid item md={6} sm={6} xs={12} key={input.key}>
                          <SchemaTextField
                            loading={input.loading}
                            disabled={input.disabled}
                            isRHF={true}
                            type={input.type}
                            name={input.key}
                            required={input.required}
                            optionLabel={addressSchemaEnum[input.key]?.LABEL}
                            optionValue={addressSchemaEnum[input.key]?.VALUE}
                            register={register}
                            options={input.options}
                            errors={errors}
                            label={input.label}
                            value={getValues(input.key)}
                            onChange={
                              getLowerCase(input.type) === inputTypesEnum.SELECT
                                ? (name, value) => {
                                    handleChangeSelectAddressSchemaAndGetOptions(
                                      input.key,
                                      index,
                                      value,
                                      setValue,
                                      input.key
                                    );
                                  }
                                : (e) => {
                                    handleChangeInputAddressSchema(
                                      input.key,
                                      e.target.value,
                                      setValue
                                    );
                                  }
                            }
                          />
                        </Grid>
                      ))}
                      <Grid
                        item
                        md={
                          schemaFieldsLength === 4 || schemaFieldsLength === 0
                            ? 12
                            : 6
                        }
                        xs={12}
                      >
                        <InputLabel sx={styleSheet.inputLabel}>Note</InputLabel>
                        <TextAreaComponent
                          value={textValue}
                          maxRows={4}
                          onChange={(e) => setTextValue(e.target.value)}
                          placeholder="Enter Note..."
                          sx={styles.noteField}
                        />
                      </Grid>
                      <Grid item md={12} xs={12} padding={"0px !important"}>
                        {isTabletOrMobile && (
                          <Box sx={{ backgroundColor: theme.secondary, pt: 1 }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              align="center"
                              color={theme.primary}
                              sx={{ my: 0.5 }}
                            >
                              Order Summary
                            </Typography>

                            <CardContent sx={{ pr: "0px !important" }}>
                              <Box
                                sx={{
                                  backgroundColor: theme.white,
                                  borderRadius: "12px",
                                  p: 1,
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mb={1.5}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    <Typography variant="h6" color={theme.text}>
                                      Price
                                    </Typography>
                                    <Tooltip title="Price before taxes and shipping">
                                      <HelpOutlineIcon
                                        sx={{
                                          fontSize: 16,
                                          color: theme.lightText,
                                        }}
                                      />
                                    </Tooltip>
                                  </Box>
                                  <Typography
                                    variant="h6"
                                    fontWeight={500}
                                    color={theme.text}
                                  >
                                    {productLinkData?.data?.Currency}{" "}
                                    {actualPrice}
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mb={1.5}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    <Typography variant="h6" color={theme.text}>
                                      Subtotal
                                    </Typography>
                                    <Tooltip title="Subtotal before taxes and shipping">
                                      <HelpOutlineIcon
                                        sx={{
                                          fontSize: 16,
                                          color: theme.lightText,
                                        }}
                                      />
                                    </Tooltip>
                                  </Box>
                                  <Typography
                                    variant="h6"
                                    fontWeight={500}
                                    color={theme.text}
                                  >
                                    {productLinkData?.data?.Currency}{" "}
                                    {actualPrice * quantity}
                                  </Typography>
                                </Box>

                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mb={1.5}
                                >
                                  <Typography color={theme.text} variant="h6">
                                    Estimated Delivery &amp; Handling
                                  </Typography>
                                  <Typography
                                    fontWeight={500}
                                    color={theme.success}
                                    variant="h6"
                                  >
                                    Free
                                  </Typography>
                                </Box>

                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mb={2}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    <Typography color={theme.text} variant="h6">
                                      Estimated Taxes
                                    </Typography>
                                    <Tooltip title="Calculated based on your location">
                                      <HelpOutlineIcon
                                        sx={{
                                          fontSize: 16,
                                          color: theme.lightText,
                                        }}
                                      />
                                    </Tooltip>
                                  </Box>
                                  <Typography
                                    fontWeight={500}
                                    color={theme.text}
                                    variant="h6"
                                  >
                                    {productLinkData?.data?.Currency} {tax}
                                  </Typography>
                                </Box>

                                <Divider sx={{ mb: 2 }} />

                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mb={1}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    color={theme.text}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    color={theme.primary}
                                  >
                                    {productLinkData?.data?.Currency}{" "}
                                    {actualPrice * quantity + tax}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item md={12} xs={12} alignSelf={"end"}>
                        <LoadingButton
                          fullWidth
                          variant="contained"
                          startIcon={<ShoppingCartIcon />}
                          sx={{
                            borderRadius: "10px",
                            mt: 3,
                            py: 1.2,
                            backgroundColor: theme.primary,
                            color: theme.white,
                            fontWeight: 600,
                            textTransform: "none",
                            boxShadow: "0 4px 10px rgba(63, 81, 181, 0.3)",
                            "&:hover": {
                              backgroundColor: theme.primaryLight,
                              boxShadow: "0 6px 12px rgba(63, 81, 181, 0.4)",
                            },
                          }}
                          loading={loading}
                          onClick={handleSubmit(PlaceOrder)}
                        >
                          Place Order
                        </LoadingButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
          }}
        >
          <Typography variant="h5" fontWeight={700} color="error" gutterBottom>
            Sorry, the page you're looking for doesn't exist.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The link may be expired or invalid.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default OrderTake;
